import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Users, 
  AlertTriangle, 
  CheckCircle, 
  Star,
  Zap,
  Award,
  MapPin,
  Wrench,
  RefreshCw,
  UserCheck,
  AlertCircle,
  TrendingUp,
  Shield,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { getActiveClient } from '@/config/platform';

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================
// TIER BADGE
// ============================================

export function ContractorTierBadge({ tier }) {
  const configs = {
    elite: { 
      label: 'Elite', 
      icon: Award,
      className: 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border-amber-300'
    },
    preferred: { 
      label: 'Preferred', 
      icon: Star,
      className: 'bg-teal-100 text-teal-700 border-teal-300'
    },
    standard: { 
      label: 'Standard', 
      icon: CheckCircle,
      className: 'bg-slate-100 text-slate-600 border-slate-300'
    },
    blocked: { 
      label: 'Blocked', 
      icon: AlertCircle,
      className: 'bg-red-100 text-red-700 border-red-300'
    }
  };

  const config = configs[tier] || configs.standard;
  const Icon = config.icon;

  return (
    <Badge 
      variant="outline"
      className={`${config.className} flex items-center gap-1`}
      data-testid={`tier-badge-${tier}`}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

// ============================================
// CAPACITY STATUS BADGE
// ============================================

export function CapacityStatusBadge({ status }) {
  const configs = {
    available: { 
      label: 'Available', 
      className: 'bg-green-100 text-green-700'
    },
    limited: { 
      label: 'Limited', 
      className: 'bg-amber-100 text-amber-700'
    },
    busy: { 
      label: 'Busy', 
      className: 'bg-orange-100 text-orange-700'
    },
    overloaded: { 
      label: 'Overloaded', 
      className: 'bg-red-100 text-red-700'
    },
    unavailable: { 
      label: 'Unavailable', 
      className: 'bg-slate-200 text-slate-600'
    },
    suspended: { 
      label: 'Suspended', 
      className: 'bg-red-200 text-red-800'
    }
  };

  const config = configs[status] || configs.available;

  return (
    <Badge 
      className={config.className}
      data-testid={`capacity-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

// ============================================
// CONTRACTOR RECOMMENDATION CARD
// ============================================

export function ContractorRecommendationCard({ 
  contractor, 
  rank, 
  onSelect, 
  selected = false,
  showWarnings = true 
}) {
  const loadPercentage = contractor.max_active_jobs > 0 
    ? (contractor.current_active_jobs / contractor.max_active_jobs) * 100 
    : 0;

  return (
    <div 
      className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
        selected 
          ? 'border-teal-500 bg-teal-50' 
          : 'border-gray-200 hover:border-teal-300 bg-white'
      }`}
      onClick={() => onSelect?.(contractor)}
      data-testid={`contractor-card-${contractor.contractor_id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {rank && (
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
              rank === 1 ? 'bg-amber-100 text-amber-700' :
              rank === 2 ? 'bg-slate-200 text-slate-600' :
              rank === 3 ? 'bg-orange-100 text-orange-700' :
              'bg-gray-100 text-gray-500'
            }`}>
              {rank}
            </div>
          )}
          <div>
            <div className="font-semibold text-gray-900">{contractor.contractor_name}</div>
            <div className="text-xs text-gray-500">{contractor.email}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-teal-600">{Math.round(contractor.score)}</div>
          <div className="text-xs text-gray-400">score</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <ContractorTierBadge tier={contractor.tier} />
        <CapacityStatusBadge status={contractor.capacity_status} />
      </div>

      {/* Load Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Workload</span>
          <span>{contractor.current_active_jobs} / {contractor.max_active_jobs} jobs</span>
        </div>
        <Progress 
          value={loadPercentage} 
          className={`h-2 ${
            loadPercentage >= 90 ? '[&>div]:bg-red-500' :
            loadPercentage >= 75 ? '[&>div]:bg-amber-500' :
            '[&>div]:bg-teal-500'
          }`}
        />
      </div>

      {/* Recommended Reason */}
      <div className="text-xs text-gray-600 mb-2">
        <CheckCircle className="h-3 w-3 inline mr-1 text-green-500" />
        {contractor.recommended_reason}
      </div>

      {/* Warnings */}
      {showWarnings && contractor.warnings?.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          {contractor.warnings.map((warning, idx) => (
            <div key={idx} className="text-xs text-amber-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {warning}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// JOB RECOMMENDATION PANEL
// ============================================

export function JobRecommendationPanel({ jobId, onAssign }) {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const loadRecommendations = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/phs-marketplace/jobs/${jobId}/marketplace-recommendations?limit=10`);
      setContractors(res.data.contractors || []);
    } catch (error) {
      console.error('Error loading recommendations:', error);
      toast.error('Failed to load contractor recommendations');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadRecommendations();
  }, [loadRecommendations]);

  const handleSelectContractor = (contractor) => {
    setSelectedContractor(contractor);
    
    // Check if capacity override needed
    if (!contractor.is_available || contractor.capacity_status === 'overloaded') {
      setShowOverrideModal(true);
    }
  };

  const handleAssign = async (overrideCapacity = false) => {
    if (!selectedContractor) return;
    setAssigning(true);
    
    try {
      await axios.post(
        `${API}/api/phs-marketplace/jobs/${jobId}/assign-contractor/${selectedContractor.contractor_id}?override_capacity=${overrideCapacity}`
      );
      toast.success(`Assigned to ${selectedContractor.contractor_name}`);
      setShowOverrideModal(false);
      onAssign?.(selectedContractor);
    } catch (error) {
      console.error('Error assigning contractor:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign contractor');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="job-recommendation-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-teal-600" />
              Recommended Contractors
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={loadRecommendations}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {contractors.length === 0 ? (
            <div className="text-center text-gray-500 py-6">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
              <p className="font-medium">No contractors available</p>
              <p className="text-sm">No contractors match this job's requirements</p>
            </div>
          ) : (
            <div className="space-y-3">
              {contractors.slice(0, 5).map((contractor, idx) => (
                <ContractorRecommendationCard
                  key={contractor.contractor_id}
                  contractor={contractor}
                  rank={idx + 1}
                  selected={selectedContractor?.contractor_id === contractor.contractor_id}
                  onSelect={handleSelectContractor}
                />
              ))}
            </div>
          )}

          {selectedContractor && selectedContractor.is_available && (
            <div className="mt-4 pt-4 border-t">
              <Button 
                className="w-full bg-teal-600 hover:bg-teal-700"
                onClick={() => handleAssign(false)}
                disabled={assigning}
                data-testid="assign-contractor-btn"
              >
                {assigning ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <UserCheck className="h-4 w-4 mr-2" />
                )}
                Assign {selectedContractor.contractor_name}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Override Modal */}
      <Dialog open={showOverrideModal} onOpenChange={setShowOverrideModal}>
        <DialogContent data-testid="capacity-override-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Capacity Override Required
            </DialogTitle>
            <DialogDescription>
              <span className="font-semibold">{selectedContractor?.contractor_name}</span> is 
              currently <span className="font-semibold text-amber-600">{selectedContractor?.capacity_status}</span>.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div className="text-sm font-medium text-amber-800 mb-2">Current Workload:</div>
              <div className="text-sm text-amber-700">
                {selectedContractor?.current_active_jobs} / {selectedContractor?.max_active_jobs} active jobs
              </div>
              <div className="text-sm text-amber-700">
                {selectedContractor?.current_jobs_today} / {selectedContractor?.max_jobs_per_day} jobs today
              </div>
            </div>
            
            {selectedContractor?.warnings?.map((warning, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {warning}
              </div>
            ))}

            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              <strong>Warning:</strong> Overriding capacity limits may lead to delayed response times 
              or contractor burnout. This action will be logged.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverrideModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleAssign(true)}
              disabled={assigning}
              data-testid="confirm-override-btn"
            >
              {assigning ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-2" />
              )}
              Override & Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================
// CONTRACTOR CAPACITY CARD
// ============================================

export function ContractorCapacityCard({ contractor }) {
  const loadPercentage = contractor.max_active_jobs > 0 
    ? (contractor.current_active_jobs / contractor.max_active_jobs) * 100 
    : 0;
  
  const dailyPercentage = contractor.max_jobs_per_day > 0
    ? (contractor.current_jobs_today / contractor.max_jobs_per_day) * 100
    : 0;

  return (
    <Card className="w-full" data-testid={`capacity-card-${contractor.contractor_id || contractor.id}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-semibold">{contractor.contractor_name || contractor.name}</div>
            <div className="text-xs text-gray-500">{contractor.email}</div>
          </div>
          <div className="flex items-center gap-2">
            <ContractorTierBadge tier={contractor.tier} />
            <CapacityStatusBadge status={contractor.capacity?.availability_status || contractor.capacity_status || 'available'} />
          </div>
        </div>

        <div className="space-y-3">
          {/* Active Jobs Progress */}
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span className="flex items-center gap-1">
                <Wrench className="h-3 w-3" />
                Active Jobs
              </span>
              <span className="font-medium">{contractor.current_active_jobs || contractor.capacity?.current_active_jobs || 0} / {contractor.max_active_jobs || contractor.capacity?.max_active_jobs || 10}</span>
            </div>
            <Progress 
              value={loadPercentage} 
              className={`h-2 ${
                loadPercentage >= 90 ? '[&>div]:bg-red-500' :
                loadPercentage >= 75 ? '[&>div]:bg-amber-500' :
                '[&>div]:bg-teal-500'
              }`}
            />
          </div>

          {/* Daily Jobs Progress */}
          <div>
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Today's Jobs
              </span>
              <span className="font-medium">{contractor.current_jobs_today || contractor.capacity?.current_jobs_today || 0} / {contractor.max_jobs_per_day || contractor.capacity?.max_jobs_per_day || 3}</span>
            </div>
            <Progress 
              value={dailyPercentage} 
              className={`h-2 ${
                dailyPercentage >= 90 ? '[&>div]:bg-red-500' :
                dailyPercentage >= 75 ? '[&>div]:bg-amber-500' :
                '[&>div]:bg-blue-500'
              }`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// COVERAGE MATRIX WIDGET
// ============================================

export function CoverageMatrixWidget() {
  const [matrix, setMatrix] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMatrix = async () => {
      try {
        const res = await axios.get(`${API}/api/phs-marketplace/ops/coverage-matrix`);
        setMatrix(res.data.matrix || []);
      } catch (error) {
        console.error('Error loading coverage matrix:', error);
      } finally {
        setLoading(false);
      }
    };
    loadMatrix();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'strong': return 'bg-green-500';
      case 'adequate': return 'bg-teal-400';
      case 'weak': return 'bg-amber-400';
      case 'none': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'strong': return 'Strong Coverage';
      case 'adequate': return 'Adequate';
      case 'weak': return 'Weak - Needs Attention';
      case 'none': return 'No Coverage!';
      default: return 'Unknown';
    }
  };

  // Group by region
  const regionGroups = matrix.reduce((acc, item) => {
    if (!acc[item.region]) acc[item.region] = [];
    acc[item.region].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get unique trades for header
  const trades = [...new Set(matrix.map(m => m.trade))];

  return (
    <Card data-testid="coverage-matrix-widget">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MapPin className="h-5 w-5 text-teal-600" />
          Region / Trade Coverage Matrix
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-2 font-medium text-gray-700">Region</th>
                {trades.slice(0, 6).map(trade => (
                  <th key={trade} className="text-center py-2 px-1 font-medium text-gray-700 whitespace-nowrap">
                    {trade.substring(0, 10)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(regionGroups).map(([region, items]) => (
                <tr key={region} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-2 font-medium">{region}</td>
                  {trades.slice(0, 6).map(trade => {
                    const cell = items.find(i => i.trade === trade);
                    return (
                      <td key={trade} className="text-center py-2 px-1">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <div className={`w-6 h-6 rounded mx-auto flex items-center justify-center text-white text-[10px] font-bold ${getStatusColor(cell?.coverage_status)}`}>
                                {cell?.available_contractors || 0}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="font-semibold">{region} - {trade}</p>
                              <p>{getStatusLabel(cell?.coverage_status)}</p>
                              <p className="text-xs mt-1">
                                Elite: {cell?.elite || 0} | Preferred: {cell?.preferred || 0} | Standard: {cell?.standard || 0}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mt-4 pt-3 border-t text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-500" />
            <span>Strong (5+)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-teal-400" />
            <span>Adequate (3-4)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-amber-400" />
            <span>Weak (1-2)</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-red-500" />
            <span>None (0)</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// MARKETPLACE OPS DASHBOARD
// ============================================

export default function MarketplaceDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/phs-marketplace/ops/marketplace-dashboard`);
      setData(res.data);
    } catch (error) {
      console.error('Error loading marketplace dashboard:', error);
      toast.error('Failed to load marketplace dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-gray-500 py-8">
            Failed to load marketplace dashboard
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="marketplace-ops-dashboard">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card className="bg-white">
          <CardContent className="p-3 text-center">
            <Users className="h-5 w-5 mx-auto text-slate-600" />
            <div className="text-2xl font-bold text-slate-700">{data.summary.total_contractors}</div>
            <div className="text-xs text-gray-500">Total</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-3 text-center">
            <CheckCircle className="h-5 w-5 mx-auto text-green-600" />
            <div className="text-2xl font-bold text-green-700">{data.summary.available}</div>
            <div className="text-xs text-green-600">Available</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50">
          <CardContent className="p-3 text-center">
            <Clock className="h-5 w-5 mx-auto text-amber-600" />
            <div className="text-2xl font-bold text-amber-700">{data.summary.limited}</div>
            <div className="text-xs text-amber-600">Limited</div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50">
          <CardContent className="p-3 text-center">
            <Zap className="h-5 w-5 mx-auto text-orange-600" />
            <div className="text-2xl font-bold text-orange-700">{data.summary.busy}</div>
            <div className="text-xs text-orange-600">Busy</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="p-3 text-center">
            <AlertTriangle className="h-5 w-5 mx-auto text-red-600" />
            <div className="text-2xl font-bold text-red-700">{data.summary.overloaded}</div>
            <div className="text-xs text-red-600">Overloaded</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-100">
          <CardContent className="p-3 text-center">
            <AlertCircle className="h-5 w-5 mx-auto text-slate-500" />
            <div className="text-2xl font-bold text-slate-600">{data.summary.unavailable}</div>
            <div className="text-xs text-slate-500">Unavailable</div>
          </CardContent>
        </Card>
        <Card className="bg-red-100">
          <CardContent className="p-3 text-center">
            <Shield className="h-5 w-5 mx-auto text-red-700" />
            <div className="text-2xl font-bold text-red-800">{data.summary.suspended}</div>
            <div className="text-xs text-red-700">Suspended</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Overloaded Contractors */}
        {data.top_overloaded?.length > 0 && (
          <Card className="border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-4 w-4" />
                Overloaded Contractors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.top_overloaded.slice(0, 5).map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-2 bg-red-50 rounded">
                    <div>
                      <div className="font-medium text-sm">{c.name}</div>
                      <div className="text-xs text-gray-500">{c.tier}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-red-700">{c.load_ratio}%</div>
                      <div className="text-xs text-gray-500">{c.current_active_jobs}/{c.max_active_jobs}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Underused Good Contractors */}
        {data.underused_good_contractors?.length > 0 && (
          <Card className="border-teal-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 text-teal-700">
                <TrendingUp className="h-4 w-4" />
                Underused Top Performers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.underused_good_contractors.slice(0, 5).map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-2 bg-teal-50 rounded">
                    <div className="flex items-center gap-2">
                      <ContractorTierBadge tier={c.tier} />
                      <div className="font-medium text-sm">{c.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-teal-700">{c.load_ratio}%</div>
                      <div className="text-xs text-gray-500">{c.current_active_jobs}/{c.max_active_jobs}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Coverage Issues */}
      {(data.no_coverage_regions?.length > 0 || data.weak_coverage_regions?.length > 0) && (
        <Card className="border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-700">
              <MapPin className="h-4 w-4" />
              Coverage Issues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              {data.no_coverage_regions?.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-red-600 mb-2">No Coverage</div>
                  <div className="flex flex-wrap gap-1">
                    {data.no_coverage_regions.map((region) => (
                      <Badge key={region} variant="destructive" className="text-xs">
                        {region}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {data.weak_coverage_regions?.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-amber-600 mb-2">Weak Coverage</div>
                  <div className="flex flex-wrap gap-1">
                    {data.weak_coverage_regions.map((r) => (
                      <Badge key={r.region} variant="outline" className="text-xs bg-amber-50">
                        {r.region} ({r.contractor_count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Blocked Jobs */}
      {data.blocked_jobs?.length > 0 && (
        <Card className="border-red-300">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              Jobs Blocked (No Contractor Available)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.blocked_jobs.slice(0, 10).map((job) => (
                <Badge key={job.id} variant="outline" className="bg-red-50 text-red-700 border-red-300">
                  {job.job_reference || job.id}
                </Badge>
              ))}
              {data.blocked_jobs.length > 10 && (
                <Badge variant="outline" className="bg-slate-50">
                  +{data.blocked_jobs.length - 10} more
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Footer */}
      <div className="text-xs text-gray-400 text-right">
        Emergency capacity available: {data.emergency_capacity_contractors} contractors • 
        Updated {new Date(data.generated_at).toLocaleTimeString()}
      </div>
    </div>
  );
}
